import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  useInternalNode,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type EdgeProps,
  type InternalNode,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react'
import clsx from 'clsx'
import { SearchIcon, XIcon } from 'lucide-react'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC, type RouterInputs, type RouterOutputs } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { useAside } from '@/layouts/Main'
import '@xyflow/react/dist/style.css'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $ from './-Schema.css'

// Навигатор по данным одной схемы: узлы — таблицы профиля (части схлопнуты в
// родителя) и понятия из памяти, рёбра — ссылки с кратностью. Старт — с поиска по
// карточкам; фокус подсвечивает соседей на шаг; панель узла — describe и память.
export const Route = createFileRoute('/data/$schema')({
  component: SchemaPage,
})

type Graph = NonNullable<RouterOutputs['layer']['graph']>
type GNode = Graph['nodes'][number]
type GEdge = Graph['edges'][number]
type Memory = RouterOutputs['layer']['memory'][number]
type AppNode = Node<{ node: GNode; w: number; dim?: boolean; hit?: boolean }, 'graph'>
type Selected = { kind: 'node'; node: GNode } | { kind: 'edge'; id: string; edge: GEdge }
type Schema = RouterInputs['layer']['graph']['schema']

let COLOR = { dict: '#64748b', fact: '#0d9488', concept: '#4f46e5' }
let colorOf = (n: GNode) => (n.kind === 'concept' ? COLOR.concept : COLOR[n.role ?? 'fact'])
let typeOf = (n: GNode) => (n.kind === 'concept' ? 'понятие' : n.role === 'dict' ? 'словарь' : 'факты')
let sizeOf = (n: GNode) => ({ w: Math.min(260, Math.max(128, n.label.length * 7.5 + 52)), h: n.kind === 'concept' ? 54 : 46 })
let num = (n: number) => n.toLocaleString('ru-RU')

let STATUS: Record<GEdge['status'], string> = {
  declared: 'объявлен (внешний ключ)',
  confirmed: 'подтверждён',
  open: 'открытый вопрос',
  rejected: 'отвергнут',
}

let edgeStroke = (e: GEdge) =>
  e.status === 'confirmed' ? '#059669' : e.status === 'rejected' ? '#e5e7eb' : e.kind === 'soft' ? '#d97706' : '#94a3b8'
let edgeDashed = (e: GEdge) => e.kind !== 'fk' && e.status !== 'confirmed'
let edgeLabel = (e: GEdge) => (e.kind === 'soft' ? `${e.overlap.matched}/${e.overlap.total}` : '')

// Handles must exist for react-flow to register edge endpoints, but floating
// edges pick their own attach point on the node border — so hide the dots.
// Styled via `style`, never `class` — a class prop clobbers Handle's own
// className and breaks edge rendering.
let HANDLE = { opacity: 0, border: 0 }

function GraphNodeView({ data, selected }: NodeProps<AppNode>) {
  let n = data.node
  return (
    <div
      class={clsx($.node, n.role === 'dict' && $.tableNode, (selected || data.hit) && $.nodeSelected)}
      style={{ width: data.w, opacity: data.dim ? 0.25 : 1 }}
    >
      <Handle type='target' position={Position.Left} style={HANDLE} />
      <span class={$.nodeDot} style={{ background: colorOf(n) }} />
      <div class={$.nodeBody}>
        <span class={$.nodeLabel}>{n.label}</span>
        <span class={$.nodeMeta}>{typeOf(n)} · {num(n.rows)}{n.parts?.length ? ` · +${n.parts.length} ч.` : ''}</span>
      </div>
      <Handle type='source' position={Position.Right} style={HANDLE} />
    </div>
  )
}

let nodeTypes = { graph: GraphNodeView }

type XY = { x: number; y: number }

// Border point of `node` on the segment towards `other` — the rhombus
// approximation from react-flow's floating-edges example.
let borderPoint = (node: InternalNode, other: InternalNode): XY => {
  let w = (node.measured.width || 200) / 2
  let h = (node.measured.height || 50) / 2
  let cx = node.internals.positionAbsolute.x + w
  let cy = node.internals.positionAbsolute.y + h
  let ox = other.internals.positionAbsolute.x + (other.measured.width || 200) / 2
  let oy = other.internals.positionAbsolute.y + (other.measured.height || 50) / 2
  let dx = (ox - cx) / (2 * w) - (oy - cy) / (2 * h)
  let dy = (ox - cx) / (2 * w) + (oy - cy) / (2 * h)
  let a = 1 / (Math.abs(dx) + Math.abs(dy) || 1)
  return { x: w * a * (dx + dy) + cx, y: h * a * (dy - dx) + cy }
}

// A floating edge attaches to whichever border faces the other endpoint, so spokes
// radiate naturally from the hub; parallel edges of one pair fan out by curvature.
function FloatingEdge({ id, source, target, markerEnd, style, label, data }: EdgeProps) {
  let sourceNode = useInternalNode(source)
  let targetNode = useInternalNode(target)
  if (!sourceNode || !targetNode) return null
  let s = borderPoint(sourceNode, targetNode)
  let t = borderPoint(targetNode, sourceNode)
  let bend = (data?.bend as number | undefined) ?? 0
  let path: string, lx: number, ly: number
  if (bend) {
    let len = Math.hypot(t.x - s.x, t.y - s.y) || 1
    let nx = -(t.y - s.y) / len, ny = (t.x - s.x) / len
    let mx = (s.x + t.x) / 2, my = (s.y + t.y) / 2
    path = `M ${s.x},${s.y} Q ${mx + nx * bend * 2},${my + ny * bend * 2} ${t.x},${t.y}`
    lx = mx + nx * bend; ly = my + ny * bend
  } else {
    ;[path, lx, ly] = getStraightPath({ sourceX: s.x, sourceY: s.y, targetX: t.x, targetY: t.y })
  }
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div class={$.edgeLabel} style={{ transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)` }}>{label}</div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

let edgeTypes = { floating: FloatingEdge }

// Radial layout: the busiest node sits at the centre, a BFS tree fans out in
// concentric rings, and children pack inside the parent's angular wedge.
let layout = (nodes: GNode[], edges: GEdge[], rootId?: string | null): Map<string, XY> => {
  let byId = new Map(nodes.map(n => [n.id, n]))
  let deg = new Map<string, number>()
  let adj = new Map(nodes.map(n => [n.id, [] as string[]]))
  for (let e of edges) {
    if (e.src === e.dst) continue
    deg.set(e.src, (deg.get(e.src) ?? 0) + 1)
    deg.set(e.dst, (deg.get(e.dst) ?? 0) + 1)
    adj.get(e.src)!.push(e.dst)
    adj.get(e.dst)!.push(e.src)
  }
  let busiest = (a: string, b: string) => (deg.get(b) ?? 0) - (deg.get(a) ?? 0)
  let root = rootId && byId.has(rootId) ? rootId : nodes.map(n => n.id).sort(busiest)[0]

  let children = new Map(nodes.map(n => [n.id, [] as string[]]))
  let depth = new Map([[root, 0]])
  for (let queue = [root]; queue.length; ) {
    let id = queue.shift()!
    for (let nb of [...new Set(adj.get(id))].sort(busiest))
      if (!depth.has(nb)) {
        depth.set(nb, depth.get(id)! + 1)
        children.get(id)!.push(nb)
        queue.push(nb)
      }
  }

  let leaves = new Map<string, number>()
  let count = (id: string): number => {
    let ch = children.get(id)!
    let c = ch.length ? ch.reduce((s, k) => s + count(k), 0) : 1
    leaves.set(id, c)
    return c
  }
  count(root)

  let ring = Math.max(380, (children.get(root)!.length * 270) / (2 * Math.PI))
  let pos = new Map<string, XY>()
  let put = (id: string, x: number, y: number) => {
    let { w, h } = sizeOf(byId.get(id)!)
    pos.set(id, { x: x - w / 2, y: y - h / 2 })
  }
  let place = (id: string, a0: number, a1: number) => {
    let r = depth.get(id)! * ring
    let mid = (a0 + a1) / 2
    put(id, Math.cos(mid) * r, Math.sin(mid) * r)
    let a = a0
    for (let k of children.get(id)!) {
      let step = ((a1 - a0) * leaves.get(k)!) / leaves.get(id)!
      place(k, a, a + step)
      a += step
    }
  }
  place(root, -Math.PI / 2, 1.5 * Math.PI)

  let maxDepth = Math.max(1, ...depth.values())
  nodes.filter(n => !pos.has(n.id)).forEach((n, i) => put(n.id, -(maxDepth * ring) - 340, -(maxDepth * ring) + i * 130))
  return pos
}

function SchemaPage() {
  let { schema } = Route.useParams() as { schema: Schema }
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes(schema) ?? false
  let isAdmin = company?.role === 'admin'
  let { data: graph, isLoading } = useQuery({ ...trpc.layer.graph.queryOptions({ schema }), enabled })

  let [selected, setSelected] = useState<Selected | null>(null)
  let [hoverId, setHoverId] = useState<string | null>(null)
  let [query, setQuery] = useState('')
  let [asked, setAsked] = useState('')
  let [show, setShow] = useState({ dict: true, fact: true, minRows: 1 })
  let [positions, setPositions] = useState<Map<string, XY>>(new Map())
  let [rf, setRf] = useState<ReactFlowInstance<AppNode, Edge> | null>(null)
  let [nodes, setNodes, onNodesChange] = useNodesState<AppNode>([])
  let [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  let { data: found } = useQuery({ ...trpc.layer.lookup.queryOptions({ schema, question: asked, kinds: ['table'] }), enabled: enabled && !!asked })
  let hits = useMemo(() => new Set((found ?? []).map(h => `table:${h.key}`)), [found])
  // Search hits count as the focus when nothing is pointed at: the graph opens from them.
  let focusId = hoverId ?? (selected?.kind === 'node' ? selected.node.id : null) ?? (found?.[0] ? `table:${found[0].key}` : null)

  let visible = useMemo(() => {
    if (!graph) return []
    let byPart = new Map(graph.nodes.flatMap(n => (n.parts ?? []).map(p => [`table:${p}`, n.id])))
    return graph.nodes.filter(n => n.kind === 'concept' || ((n.role === 'dict' ? show.dict : show.fact) && n.rows >= show.minRows) || hits.has(n.id) || hits.has(byPart.get(n.id) ?? ''))
  }, [graph, show, hits])
  let topEdges = useMemo(() => {
    let ids = new Set(visible.map(n => n.id))
    return (graph?.edges ?? []).filter(e => !e.internal && e.status !== 'rejected' && ids.has(e.src) && ids.has(e.dst))
  }, [graph, visible])
  let structure = useMemo(
    () => visible.map(n => n.id).join('|') + '#' + topEdges.map(e => `${e.src}>${e.dst}`).join('|') + '#' + (found?.[0]?.key ?? ''),
    [visible, topEdges, found],
  )

  useEffect(() => {
    setPositions(visible.length ? layout(visible, topEdges, found?.[0] ? `table:${found[0].key}` : null) : new Map())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure])

  useEffect(() => {
    setNodes(visible.filter(n => positions.has(n.id)).map(n => ({
      id: n.id, type: 'graph', position: positions.get(n.id)!, data: { node: n, w: sizeOf(n).w, hit: hits.has(n.id) },
    })))
  }, [visible, positions, hits, setNodes])

  useEffect(() => {
    let active = new Set<string>()
    if (focusId) {
      active.add(focusId)
      for (let e of topEdges) {
        if (e.src === focusId) active.add(e.dst)
        if (e.dst === focusId) active.add(e.src)
      }
    }
    setNodes(nds => nds.map(n => {
      let dim = focusId ? !active.has(n.id) && !hits.has(n.id) : false
      return n.data.dim === dim ? n : { ...n, data: { ...n.data, dim } }
    }))
  }, [focusId, topEdges, hits, setNodes])

  useEffect(() => {
    let pairs = new Map<string, number[]>()
    topEdges.forEach((e, i) => {
      let k = [e.src, e.dst].sort().join('\t')
      pairs.set(k, [...(pairs.get(k) ?? []), i])
    })
    let bends = new Map<number, number>()
    for (let idx of pairs.values())
      idx.forEach((ei, j) => bends.set(ei, (j - (idx.length - 1) / 2) * 42 * (topEdges[ei].src < topEdges[ei].dst ? 1 : -1)))

    setEdges(topEdges.map((e, i) => {
      let incident = !!focusId && (e.src === focusId || e.dst === focusId)
      let faded = !!focusId && !incident
      let stroke = incident ? edgeStroke(e) : '#c9ced6'
      return {
        id: `e${i}`, type: 'floating', source: e.src, target: e.dst,
        data: { edge: e, bend: bends.get(i) ?? 0 },
        label: incident ? edgeLabel(e) || undefined : undefined,
        style: { stroke, strokeWidth: incident ? 2 : 1, strokeDasharray: edgeDashed(e) ? '5 4' : undefined, opacity: faded ? 0.12 : focusId ? 1 : 0.6, transition: 'stroke 0.2s, stroke-width 0.2s, opacity 0.2s' },
        markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 13, height: 13 },
      }
    }))
  }, [topEdges, focusId, setEdges])

  useEffect(() => { if (rf && positions.size) rf.fitView({ padding: 0.2 }) }, [rf, positions])

  useAside(() => selected ? <DetailPanel schema={schema} selected={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} /> : null, [selected, isAdmin, schema])

  if (!enabled) return <div class={$layout.container}><p class={$type.muted}>Раздел недоступен.</p></div>

  let counts = graph
    ? { concepts: graph.nodes.filter(n => n.kind === 'concept').length, tables: visible.filter(n => n.kind === 'table').length, all: graph.nodes.filter(n => n.kind === 'table').length }
    : null

  return (
    <div class={$.page}>
      <form class={$.toolbar} onSubmit={e => { e.preventDefault(); setAsked(query.trim()); setSelected(null) }}>
        <SearchIcon size={16} />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder='Что искать: выручка, остатки, контрагент…' />
        {asked && <button type='button' class='plain' onClick={() => { setQuery(''); setAsked('') }} aria-label='Сбросить'><XIcon size={14} /></button>}
        <label><input type='checkbox' checked={show.dict} onChange={e => setShow({ ...show, dict: e.target.checked })} /> словари</label>
        <label><input type='checkbox' checked={show.fact} onChange={e => setShow({ ...show, fact: e.target.checked })} /> факты</label>
        <label>от <input type='number' min={0} value={show.minRows} onChange={e => setShow({ ...show, minRows: Number(e.target.value) || 0 })} /> строк</label>
        {counts && <span class={$type.muted}>{counts.tables} из {counts.all} таблиц · {counts.concepts} понятий</span>}
      </form>
      <div class={$.canvas}>
        {isLoading ? (
          <div class={$.empty}><Spinner /></div>
        ) : !graph || !graph.nodes.length ? (
          <div class={$.empty}><span class={$type.muted}>Схема ещё не профилирована.</span></div>
        ) : (
          <ReactFlow<AppNode, Edge>
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes} edgeTypes={edgeTypes}
            onInit={setRf}
            nodesConnectable={false}
            onNodeMouseEnter={(_, node) => setHoverId(node.id)}
            onNodeMouseLeave={() => setHoverId(null)}
            onNodeClick={(_, node) => setSelected({ kind: 'node', node: (node as AppNode).data.node })}
            onEdgeClick={(_, edge) => setSelected({ kind: 'edge', id: edge.id, edge: (edge.data as { edge: GEdge }).edge })}
            onPaneClick={() => { setSelected(null); setHoverId(null) }}
            minZoom={0.1}
            fitView
          >
            <Background gap={22} color='#f4f5f7' />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}
      </div>
    </div>
  )
}

function DetailPanel({ schema, selected, isAdmin, onClose }: { schema: Schema; selected: Selected; isAdmin: boolean; onClose: () => void }) {
  return (
    <div class={$.panel}>
      <button type='button' class={clsx('plain', $.panelClose)} onClick={onClose} aria-label='Закрыть'><XIcon size={16} /></button>
      {selected.kind === 'node'
        ? <NodeDetail schema={schema} node={selected.node} isAdmin={isAdmin} />
        : <EdgeDetail schema={schema} edge={selected.edge} isAdmin={isAdmin} />}
    </div>
  )
}

function NodeDetail({ schema, node, isAdmin }: { schema: Schema; node: GNode; isAdmin: boolean }) {
  let tables = node.kind === 'concept' ? (node.tables ?? []) : [node.label, ...(node.parts ?? [])]
  return (
    <>
      <div class={$.panelHead}>
        <span class={$.nodeDot} style={{ background: colorOf(node) }} />
        <h4>{node.label}</h4>
      </div>
      <p class={$type.muted}>{typeOf(node)} · {num(node.rows)} строк{node.kind === 'concept' ? ` · ${tables.length} табл.` : node.parts?.length ? ` · частей: ${node.parts.length}` : ''}</p>
      {node.description && <p>{node.description}</p>}
      <MemoryList schema={schema} tables={tables} isAdmin={isAdmin} />
      <div class={$.tables}>
        {tables.map(t => <TableProfile key={t} schema={schema} name={t} />)}
      </div>
    </>
  )
}

let STATUS_RU: Record<Memory['status'], string> = { draft: 'черновик', proposed: 'предложено', verified: 'подтверждено', stale: 'устарело' }
let ORIGIN_RU: Record<Memory['origin'], string> = { declared: 'источник', inferred: 'выведено', verified: 'проверено', decided: 'решено' }

// Память узла: заметки и ответы с происхождением и статусом; администратор
// подтверждает, отвергает и добавляет решённое.
function MemoryList({ schema, tables, isAdmin }: { schema: Schema; tables: string[]; isAdmin: boolean }) {
  let trpc = useTRPC()
  let qc = useQueryClient()
  let [text, setText] = useState('')
  let [concept, setConcept] = useState(false)
  let { data: memory } = useQuery(trpc.layer.memory.queryOptions({ schema, tables }))
  let refresh = () => {
    void qc.invalidateQueries({ queryKey: trpc.layer.memory.queryKey({ schema, tables }) })
    void qc.invalidateQueries({ queryKey: trpc.layer.graph.queryKey({ schema }) })
  }
  let setStatus = useMutation(trpc.layer.setStatus.mutationOptions({ onSuccess: refresh }))
  let remember = useMutation(trpc.layer.remember.mutationOptions({ onSuccess: () => { setText(''); refresh() } }))
  let items = (memory ?? []).filter(m => !m.meta?.path)
  return (
    <div class={$.memory}>
      {items.map(m => (
        <div key={m.key} class={clsx($.memoryItem, m.status === 'stale' && $.memoryStale)}>
          <div class={$.memoryText}>{m.text}</div>
          <div class={$.memoryMeta}>
            <span>{m.kind === 'answer' ? 'ответ' : m.meta?.concept ? 'понятие' : 'заметка'} · {ORIGIN_RU[m.origin]} · {STATUS_RU[m.status]}</span>
            {isAdmin && m.status !== 'verified' && (
              <button type='button' class='plain' disabled={setStatus.isPending} onClick={() => setStatus.mutate({ schema, kind: m.kind === 'answer' ? 'answer' : 'note', key: m.key, status: 'verified' })}>подтвердить</button>
            )}
            {isAdmin && m.status !== 'stale' && (
              <button type='button' class='plain' disabled={setStatus.isPending} onClick={() => setStatus.mutate({ schema, kind: m.kind === 'answer' ? 'answer' : 'note', key: m.key, status: 'stale' })}>отвергнуть</button>
            )}
          </div>
        </div>
      ))}
      {isAdmin && (
        <form class={$.memoryForm} onSubmit={e => { e.preventDefault(); if (text.trim()) remember.mutate({ schema, text: text.trim(), tables, concept: concept && tables.length > 1 }) }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder='Что нужно знать об этих данных: определение, как считать, чего не брать' />
          {tables.length > 1 && <label><input type='checkbox' checked={concept} onChange={e => setConcept(e.target.checked)} /> понятие — сгруппировать эти таблицы в графе</label>}
          <button type='submit' disabled={remember.isPending || !text.trim()}>Запомнить</button>
        </form>
      )}
    </div>
  )
}

function TableProfile({ schema, name }: { schema: Schema; name: string }) {
  let trpc = useTRPC()
  let [open, setOpen] = useState(false)
  let { data: text, isLoading } = useQuery({ ...trpc.layer.describe.queryOptions({ schema, table: name }), enabled: open })
  return (
    <details class={$.table} onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{name}</summary>
      {open && (isLoading ? <Spinner /> : <pre class={$.describe}>{text ?? '—'}</pre>)}
    </details>
  )
}

function EdgeDetail({ schema, edge, isAdmin }: { schema: Schema; edge: GEdge; isAdmin: boolean }) {
  let trpc = useTRPC()
  let qc = useQueryClient()
  let remember = useMutation(trpc.layer.remember.mutationOptions({
    onSuccess: () => void qc.invalidateQueries({ queryKey: trpc.layer.graph.queryKey({ schema }) }),
  }))
  let verdict = (v: 'confirmed' | 'rejected') => remember.mutate({
    schema, path: edge.path, verdict: v, tables: [edge.path.split('.')[0]],
    text: `Связь ${edge.via} — ${v === 'confirmed' ? 'подтверждена' : 'ложная'}`,
  })
  return (
    <>
      <div class={$.panelHead}><h4>Связь</h4></div>
      <p class={$type.muted}>{edge.kind === 'fk' ? 'внешний ключ' : 'найдена по данным'} · {STATUS[edge.status]}</p>
      <p class={$type.small}>{edge.via}</p>
      <p class={$type.small}>Совпадение значений: {edge.overlap.matched}/{edge.overlap.total}</p>
      {isAdmin && edge.kind !== 'fk' && (
        <div class={$.actions}>
          <button type='button' disabled={remember.isPending} onClick={() => verdict('confirmed')}>Подтвердить</button>
          <button type='button' disabled={remember.isPending} onClick={() => verdict('rejected')}>Отвергнуть</button>
        </div>
      )}
    </>
  )
}
